import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, HandCoins, HeartHandshake, CalendarCheck, GraduationCap, UsersRound, Wallet, Home, Megaphone, Inbox, Settings, LogOut, Menu, X, KeyRound, UserCheck,
} from "lucide-react";
import { useState } from "react";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { AccountSettingsDialog } from "@/components/AccountSettingsDialog";
import { UserCog, UserCircle2, Eye } from "lucide-react";
import { canSeeFinance, isReadOnly } from "@/lib/access";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/app/members", label: "Members", icon: Users, testid: "nav-members" },
  { to: "/app/approvals", label: "Approvals", icon: UserCheck, testid: "nav-approvals", adminOnly: true },
  { to: "/app/families", label: "Families", icon: Home, testid: "nav-families" },
  { to: "/app/donations", label: "Donations", icon: HandCoins, testid: "nav-donations", finance: true },
  { to: "/app/finances", label: "Finances", icon: Wallet, testid: "nav-finances", finance: true },
  { to: "/app/ministries", label: "Ministries", icon: HeartHandshake, testid: "nav-ministries" },
  { to: "/app/attendance", label: "Attendance", icon: CalendarCheck, testid: "nav-attendance" },
  { to: "/app/trainings", label: "Trainings", icon: GraduationCap, testid: "nav-trainings" },
  { to: "/app/announcements", label: "Announcements", icon: Megaphone, testid: "nav-announcements" },
  { to: "/app/messages", label: "Messages", icon: Inbox, testid: "nav-messages" },
  { to: "/app/settings", label: "Site Content", icon: Settings, testid: "nav-settings", adminOnly: true },
  { to: "/app/team", label: "Team", icon: UsersRound, testid: "nav-team", adminOnly: true },
];

const TITLES = {
  "/app": "Dashboard",
  "/app/members": "Members Directory",
  "/app/approvals": "Pending Approvals",
  "/app/families": "Families",
  "/app/donations": "Tithes & Offerings",
  "/app/finances": "Finances",
  "/app/ministries": "Ministries",
  "/app/attendance": "Attendance",
  "/app/trainings": "Trainings",
  "/app/announcements": "Events & Announcements",
  "/app/messages": "Contact Messages",
  "/app/settings": "Site Content",
  "/app/team": "Team & Access",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);

  const SidebarInner = () => (
    <div className="flex h-full flex-col justify-between">
      <div>
        <div className="flex items-center gap-3 px-6 py-6">
          <img src="/whf-emblem.png" alt="World Harvest Fellowship" className="h-11 w-11 shrink-0 object-contain" />
          <div className="min-w-0 leading-tight">
            <p className="font-serif text-lg font-semibold text-stone-50">World Harvest</p>
            <p className="text-[11px] uppercase tracking-widest text-[#E7C65A]">Fellowship · Auckland</p>
          </div>
        </div>
        <nav className="mt-4 px-3 space-y-1">
          {NAV.filter((item) => (!item.adminOnly || user?.role === "admin") && (!item.finance || canSeeFinance(user))).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#2D5A27] text-white"
                    : "text-stone-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </NavLink>
          ))}
          {user?.member_id && (
            <NavLink to="/portal" data-testid="nav-my-profile" onClick={() => setOpen(false)}
              className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-[#E7C65A] hover:bg-white/5 transition-colors">
              <UserCircle2 className="h-5 w-5" /> My Profile
            </NavLink>
          )}
        </nav>
      </div>
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 px-2 py-3">
          <div className="h-9 w-9 rounded-full bg-[#D97706] flex items-center justify-center text-white text-sm font-semibold">
            {(user?.name || "A").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-stone-100">{user?.name}</p>
            <p className="truncate text-xs text-stone-400">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={() => { setAcctOpen(true); setOpen(false); }}
          data-testid="account-settings-button"
          className="mt-1 flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-stone-300 hover:bg-white/5 hover:text-white transition-colors"
        >
          <UserCog className="h-5 w-5" /> Account settings
        </button>
        <button
          onClick={() => { setPwOpen(true); setOpen(false); }}
          data-testid="change-password-button"
          className="mt-1 flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-stone-300 hover:bg-white/5 hover:text-white transition-colors"
        >
          <KeyRound className="h-5 w-5" /> Change password
        </button>
        <button
          onClick={logout}
          data-testid="logout-button"
          className="mt-1 flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-stone-300 hover:bg-white/5 hover:text-white transition-colors"
        >
          <LogOut className="h-5 w-5" /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen bg-[#FBF9F5] ${isReadOnly(user) ? "readonly-mode" : ""}`}>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-[#1A2619] shadow-xl flex-col overflow-y-auto">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-[#1A2619] shadow-xl overflow-y-auto">
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-[#E5E0D8]">
          <div className="flex items-center justify-between px-4 sm:px-8 h-16">
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden p-2 -ml-2 text-[#1C241B]"
                onClick={() => setOpen((v) => !v)}
                data-testid="mobile-menu-toggle"
              >
                {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
              <h1 className="font-serif text-xl sm:text-2xl font-semibold text-[#1C241B]">
                {TITLES[location.pathname] || "FlockCare"}
              </h1>
            </div>
            <span className="hidden sm:block text-xs font-semibold uppercase tracking-widest text-[#2D5A27]">
              World Harvest Fellowship · Auckland
            </span>
          </div>
        </header>
        <main className="px-4 sm:px-8 py-6 sm:py-8 max-w-7xl mx-auto">
          {isReadOnly(user) && (
            <div className="mb-6 flex items-center gap-2 rounded-xl border border-[#BAE6FD] bg-[#E0F2FE] px-4 py-3 text-sm text-[#0369A1]" data-testid="readonly-banner">
              <Eye className="h-4 w-4 shrink-0" />
              <span><b>{user.church_role}</b> · read-only access{user.access === "read_nonfin" ? " (financials hidden)" : ""}. You can view information but not change it.</span>
            </div>
          )}
          <Outlet />
        </main>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <AccountSettingsDialog open={acctOpen} onOpenChange={setAcctOpen} />
    </div>
  );
}
